/**
 * Toast — the locked toast primitive (ink fill, e-2, good-on-dark tick).
 * Slide-up 300ms; reduced-motion handled by base.css.
 */
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface ToastItem {
  id: number;
  msg: string;
  ok?: boolean;
}

interface ToastApi {
  toast: (msg: string, opts?: { ok?: boolean }) => void;
}

const Ctx = createContext<ToastApi | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const toast = useCallback((msg: string, opts?: { ok?: boolean }) => {
    const id = ++seq.current;
    setItems((x) => [...x, { id, msg, ok: opts?.ok }]);
    window.setTimeout(() => {
      setItems((x) => x.filter((t) => t.id !== id));
    }, 3200);
  }, []);

  return (
    <Ctx.Provider value={{ toast }}>
      {children}
      <div className="toast-host" aria-live="polite" aria-atomic="true">
        {items.map((t) => (
          <div className="toast" key={t.id} role="status">
            {t.ok && <span className="tick" aria-hidden>✓</span>}
            <span>{t.msg}</span>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useToast must be used within <ToastProvider>');
  return v;
}
